import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AlertTriangle,
  CalendarDays,
  Car,
  CheckCircle2,
  CircleDashed,
  ClipboardList,
  Clock,
  Download,
  FileText,
  Info,
  Link2,
  Loader2,
  Star,
  Timer,
  TimerOff,
  Wrench,
  XCircle,
} from "lucide-react";
import { getAnalyticsData } from "../services/apiService";
import { getClientEntry } from "../config/clientConfig";
import { getSourceName } from "../services/authService";
import { getStatusInfoFor } from "../utils/statusMapping";
import { toAnalytics, formatDuration } from "./analyticsData";
import {
  applyFilters,
  EMPTY_FILTERS,
  facetsOf,
  sortInspections,
  toInspections,
} from "./inspections";
import { buildReportHtml, reportFileName, reportTitle } from "./report";
import { downloadFile, printHtml } from "./download";
import InspectionsTable from "./InspectionsTable";

/**
 * Ergo's inspection analytics, motor or property depending on the session.
 *
 * Colour does one job here. Every chart is a single series measuring magnitude,
 * so they share one sequential hue; the one status colour is reserved for the
 * thing that has gone wrong, which is what should catch the eye. That is also
 * why there is no legend anywhere — a legend for one series is a box saying
 * what the title already said.
 *
 * Both fills validated against the palette checks (CVD ΔE 23.8 worst case,
 * 31.6 normal vision, both >= 3:1 on this surface).
 */

/**
 * Colour on this page, and the evidence behind it.
 *
 * Every ramp below was run through the palette validator; only values it passed
 * are ever rendered. Three steps per hue rather than an interpolation, so no
 * colour reaches the screen that was not checked:
 *
 *   #2a78d6 #2369c0 #1c5aa9   #c0399a #ad3189 #992a78
 *   #a37f12 #8c6c0f #75590c   #d03b3b #bc3232 #a52b2b
 *   -> lightness band PASS, contrast vs surface PASS (all >= 3:1) for each
 *
 * WITHIN a chart there is never a categorical encoding: one hue, and depth
 * carries magnitude. That is what keeps it readable — the validator is blunt
 * about what does not work, and it is worth writing down, because all three
 * are the obvious "colourful" designs:
 *
 *   green + red    deltaE 3.4 deutan   the traffic light is the worst offender
 *   amber + red    deltaE 2.5 deutan   as is any warn-vs-fail pair
 *   magenta + red  deltaE 14.4 normal  below the hard floor for full colour vision
 *
 * So the funnel is NOT a green-to-red ramp, and the only pair that ever shares
 * one chart is blue + red (deltaE 23.8 CVD, 31.6 normal — comfortably clear).
 *
 * ACROSS cards, hue names the dimension: blue counts things, magenta is damage,
 * amber is a capture warning, red is a failure. Amber and red would fail as a
 * palette pair, and never appear in one chart for that reason; each card also
 * carries a title and an icon, so hue is never the only thing saying what a
 * card measures.
 *
 * Status colour (the tiles, the expired row, a failed result) follows the other
 * rule: it always ships with an icon and a word, never colour alone.
 */
const RAMP = {
  blue: ["#2a78d6", "#2369c0", "#1c5aa9"],
  magenta: ["#c0399a", "#ad3189", "#992a78"],
  amber: ["#a37f12", "#8c6c0f", "#75590c"],
  red: ["#d03b3b", "#bc3232", "#a52b2b"],
  green: ["#1d9a50", "#198746", "#15743c"],
};

const FILL = RAMP.blue[0];
const ALERT = RAMP.red[0];
const GOOD = RAMP.green[0];
/**
 * The star glyph's colour.
 *
 * The page's amber, reused deliberately: a gold star is a strong enough
 * convention that inventing a fifth hue for it would be worse, and a fifth hue
 * does not survive the all-pairs check anyway — violet measures deltaE 2.5
 * (deutan) against the blue, and #8a6d1f, the obvious darker gold, is under the
 * chroma floor and reads grey. Stars are a glyph rather than a bar, so nothing
 * here is compared against the warning bars that share the hue.
 *
 * It is deliberately NOT green or red: 3 out of 5 is neither good news nor bad,
 * and colouring it as either states an opinion the data has not earned.
 */
const RATING = RAMP.amber[0];

/**
 * Ink, not a mark.
 *
 * A fill only has to clear 3:1 against the surface; a word has to clear 4.5:1,
 * and #d03b3b measures 4.45:1 as text on the app's plane — just under. These
 * are the app's own chip foregrounds (--cl-warn-chip-fg, --cl-status-rejected-fg)
 * at 5.81:1 and 5.61:1, so a failed inspection also reads the same here as it
 * does on the case list.
 */
const WARN_INK = "#8a5c07";
const GOOD_INK = "#187a3c"; // --cl-success-chip-fg, 5.40:1 as a word
const ALERT_INK = "#c22f2f"; // --cl-status-rejected-fg, 5.61:1 as a word
const INK_MUTED = "var(--cl-text-muted)";

/**
 * A step of an ordinal ramp, for a category that has an order.
 *
 * Only for ORDERED categories — rating tiers, and nothing else on this page.
 * Colouring nominal categories (warning codes, panel names, damage types)
 * darker-where-bigger is a named anti-pattern: it double-encodes bar length as
 * hue and spends the one free channel on what the bar already says. BarRow
 * therefore takes a single colour and cannot ramp on its own; a caller with
 * ordered tiers passes this per row.
 *
 * Validated as an ordinal ramp: lightness monotone, adjacent dL >= 0.06,
 * light end 3.65:1 on the surface, hue spread 2 degrees.
 */
const depth = (ramp, tier, tiers) => {
  if (!(tiers > 0)) return ramp[0];
  const share = tier / tiers;
  return share > 0.66 ? ramp[2] : share > 0.33 ? ramp[1] : ramp[0];
};

/** A hue at 8% over the surface — for an icon chip, never for a data mark. */
const wash = (hex) => `${hex}14`;

/**
 * Says when a list is only showing its head.
 *
 * These cards render the top 8. Motor returns 21 distinct panel names, so 13
 * were dropping out with nothing to say they had — which reads as "these are
 * all of them". A cap the reader cannot see is worse than a longer list.
 */
const capped = (total, shown, otherwise) =>
  total > shown ? `top ${shown} of ${total}` : otherwise;

const card = "rounded-card border border-cl-card bg-cl-surface overflow-hidden";
const cardHead =
  "flex items-baseline justify-between gap-3 border-b border-cl-head px-4 py-[11px]";
const cardTitle = "text-[13.5px] font-semibold text-cl-text";
// Named hintClass because CardHead takes a `hint` prop.
// text-cl-meta, not text-cl-faint: faint measures 2.63:1 on this surface, and
// these hints carry real information — the caps, the coverage, the peak.
const hintClass = "text-[11px] text-cl-meta";

const pct = (value) =>
  value === null || value === undefined ? "—" : `${Math.round(value * 100)}%`;

const iso = (date) => date.toISOString().slice(0, 10);

/** A headline figure. Not a chart: one number needs no axes. */
export function Tile({ label, value, sub, tone, icon: Icon, hue = FILL, visual }) {
  // A tone is a status, so it arrives with an icon beside the word — the tile
  // never asks a reader to know that this particular red means trouble.
  const toneColor =
    tone === "alert" ? ALERT_INK : tone === "good" ? GOOD_INK : "var(--cl-text)";
  const accent = tone === "alert" ? ALERT : tone === "good" ? GOOD : hue;
  return (
    <div
      className={`${card} relative overflow-hidden px-4 py-[13px] pl-[17px]`}
      style={{ background: `linear-gradient(180deg, ${accent}0f, ${accent}05)` }}
    >
      {/* The rail is the tile's colour worn where it cannot be mistaken for a
          data mark: it is the same width on every tile and encodes nothing. */}
      <span
        className="absolute bottom-0 left-0 top-0 w-[3px]"
        style={{ background: accent }}
      />
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-[7px]">
            {Icon && (
              <span
                className="flex h-[20px] w-[20px] flex-none items-center justify-center rounded-[6px]"
                style={{ background: `${accent}1f`, color: accent }}
              >
                <Icon className="h-[12px] w-[12px]" />
              </span>
            )}
            <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label">
              {label}
            </div>
          </div>
          <div
            className="mt-[5px] text-[23px] font-bold leading-none tracking-[-0.01em]"
            style={{ color: toneColor }}
          >
            {value}
          </div>
          {sub && (
            <div className="mt-[4px] text-[11.5px] text-cl-meta">{sub}</div>
          )}
        </div>
        {visual && <div className="mt-[2px] flex-none">{visual}</div>}
      </div>
    </div>
  );
}

/**
 * A ratio as a ring.
 *
 * A percentage is a part of a whole and a ring says so at a glance, which the
 * number alone cannot. The number stays written beside it, so nothing here is
 * readable by arc length only.
 */
export function Arc({ value, color, size = 40, width = 4.5 }) {
  const radius = (size - width) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = Math.max(0, Math.min(1, value || 0)) * circumference;
  const centre = size / 2;
  return (
    <svg width={size} height={size} aria-hidden="true" className="flex-none">
      <circle
        cx={centre}
        cy={centre}
        r={radius}
        fill="none"
        stroke={`${color}26`}
        strokeWidth={width}
      />
      <circle
        cx={centre}
        cy={centre}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={width}
        strokeLinecap="round"
        strokeDasharray={`${filled} ${circumference}`}
        transform={`rotate(-90 ${centre} ${centre})`}
      />
    </svg>
  );
}

/**
 * The shape of the period, small enough to sit in a tile.
 *
 * The same numbers as the chart below, so it carries no axis and no labels — it
 * is here to say "steady" or "one big day" before the reader scrolls.
 */
export function Sparkline({ points, color, width = 78, height = 26 }) {
  if (!points || points.length < 2) return null;
  const max = Math.max(...points.map((point) => point.count), 1);
  const step = width / (points.length - 1);
  const y = (count) => height - 1.5 - (count / max) * (height - 3);
  const line = points
    .map((point, index) => `${index ? "L" : "M"}${index * step},${y(point.count)}`)
    .join(" ");
  return (
    <svg width={width} height={height} aria-hidden="true" className="flex-none">
      <path d={`${line} L${width},${height} L0,${height} Z`} fill={`${color}1f`} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={1.75}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * A rating as five stars plus the number.
 *
 * The number is the precise part and the stars are what makes it legible at a
 * glance; a half-filled star would be a lie the data does not support, so the
 * scale is rounded to whole stars and the exact mean is written beside it.
 */
export function Stars({ value, size = 13 }) {
  const filled = Math.round(value);
  return (
    <span className="inline-flex items-center gap-[2px]" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((step) => (
        <Star
          key={step}
          style={{
            width: size,
            height: size,
            color: step <= filled ? RATING : "var(--cl-sev-none)",
            fill: step <= filled ? RATING : "transparent",
          }}
        />
      ))}
    </span>
  );
}

/**
 * The rating headline.
 *
 * Deliberately shaped so an absent rating is as legible as a present one. On
 * the live data ratings are the rarest field the endpoint returns — 11 of 188
 * motor inspections, 1 of 5 property — so a mean with no denominator beside it
 * would be the most misleading number on the page.
 */
export function RatingTile({ rating }) {
  const { mean, recorded, of } = rating || {};
  if (!recorded) {
    return (
      <div className={`${card} px-4 py-[13px]`}>
        <div className="flex items-center gap-[7px]">
          <span
            className="flex h-[20px] w-[20px] flex-none items-center justify-center rounded-[6px]"
            style={{ background: wash(RATING), color: RATING }}
          >
            <Star className="h-[12px] w-[12px]" />
          </span>
          <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label">
            Surveyor rating
          </div>
        </div>
        <div className="mt-[5px] text-[22px] font-bold leading-none text-cl-disabled">
          —
        </div>
        <div className="mt-[4px] text-[11.5px] text-cl-muted">
          nobody rated an inspection in this period
        </div>
      </div>
    );
  }

  return (
    <div className={`${card} px-4 py-[13px]`}>
      <div className="flex items-center gap-[7px]">
        <span
          className="flex h-[20px] w-[20px] flex-none items-center justify-center rounded-[6px]"
          style={{ background: wash(RATING), color: RATING }}
        >
          <Star className="h-[12px] w-[12px]" />
        </span>
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label">
          Surveyor rating
        </div>
      </div>
      <div className="mt-[5px] flex items-baseline gap-[7px]">
        <span className="text-[22px] font-bold leading-none tracking-[-0.01em] text-cl-text">
          {mean.toFixed(1)}
        </span>
        <Stars value={mean} />
      </div>
      <div className="mt-[4px] text-[11.5px] text-cl-muted">
        given on {recorded} of {of} read
      </div>
    </div>
  );
}

/**
 * A card's heading, with the hue that names its dimension.
 *
 * The icon and the title say what the card measures; the colour only makes it
 * quicker to find again.
 */
export function CardHead({ icon: Icon, title, hint, hue = FILL }) {
  return (
    <div
      className={cardHead}
      // The header wears the card's hue; the title stays in ink, because text
      // in a series colour is how a page stops being readable.
      style={{
        background: `linear-gradient(180deg, ${hue}14, ${hue}08)`,
        borderBottomColor: `${hue}26`,
      }}
    >
      <span className="flex items-center gap-[7px]">
        <span
          className="flex h-[21px] w-[21px] flex-none items-center justify-center rounded-[6px]"
          style={{ background: wash(hue), color: hue }}
        >
          <Icon className="h-[12px] w-[12px]" />
        </span>
        <span className={cardTitle}>{title}</span>
      </span>
      {hint && <span className={hintClass}>{hint}</span>}
    </div>
  );
}

/**
 * A ranked row: label, bar, value.
 *
 * The value is always written next to the bar, so the chart is its own table —
 * nothing here is readable only by comparing lengths, and nothing depends on
 * colour to be understood.
 */
export function BarRow({
  label,
  sub,
  count,
  max,
  color = FILL,
  icon: Icon,
  onClick,
  active,
}) {
  // The track is a wash of the bar's own hue rather than grey — colour that
  // encodes nothing, since every track in a card is identical. Checked: all
  // four hues still clear 3:1 against a tint this light.
  const track = `${color}1a`;
  const width = max > 0 ? Math.max(2, (count / max) * 100) : 0;
  // A bar that filters the list below is a button; one that does not stays a
  // div, so nothing advertises an interaction it does not have.
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      aria-pressed={onClick ? Boolean(active) : undefined}
      className={`flex w-full items-center gap-3 px-4 py-[7px] text-left ${
        onClick ? "transition-colors hover:bg-cl-hover" : ""
      } ${active ? "bg-cl-hover" : ""}`}
    >
      <div className="min-w-0 flex-1">
        <div
          className="flex items-center gap-[6px] text-[12.5px] font-medium text-cl-text"
          title={label}
        >
          {Icon && (
            <Icon className="h-[12px] w-[12px] flex-none" style={{ color }} />
          )}
          <span className="truncate">{label}</span>
        </div>
        {sub && <div className="truncate text-[11px] text-cl-faint">{sub}</div>}
      </div>
      <div
        className="h-[9px] w-[26%] flex-none overflow-hidden rounded-[5px]"
        style={{ background: track }}
      >
        <div
          className="h-full rounded-[5px]"
          style={{
            width: `${width}%`,
            background: color,
            // A sheen down the bar, not along it: gradients that run with the
            // length make colour vary with the value all over again.
            backgroundImage:
              "linear-gradient(180deg, rgba(255,255,255,0.24), rgba(255,255,255,0) 70%)",
          }}
        />
      </div>
      <div className="w-[38px] flex-none text-right text-[12.5px] font-bold text-cl-secondary">
        {count}
      </div>
    </Tag>
  );
}

/**
 * Inspections per day.
 *
 * Bars because the values are counts on discrete days, not a continuous
 * signal. Only the ends of the range are labelled — a number over every bar is
 * noise, and each bar carries its own on hover.
 */
const PLOT_HEIGHT = 110;
const MIN_BAR = 7;
const MAX_BAR_WIDTH = 54;

export function DailyVolume({ daily, selectedDay, onSelectDay }) {
  const max = Math.max(...daily.map((d) => d.count), 1);
  const width = 100 / Math.max(daily.length, 1);
  const Bar = onSelectDay ? "button" : "div";

  // One day is not a distribution. A lone bar in an empty plot invites a
  // comparison there is nothing to compare, so it says the number instead —
  // which is what a reader would take away from the chart anyway.
  if (daily.length === 1) {
    const [only] = daily;
    return (
      <div className="px-4 pb-[15px] pt-[13px]">
        <p className="text-[12.5px] leading-[1.55] text-cl-secondary">
          All {only.count} inspection{only.count === 1 ? "" : "s"} in this period
          were captured on <span className="font-semibold">{only.date}</span>.
        </p>
        {onSelectDay && (
          <button
            type="button"
            onClick={() => onSelectDay(selectedDay === only.date ? null : only.date)}
            className="mt-[9px] rounded-tile border border-cl-control px-[11px] py-[5px] text-[11.5px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover"
          >
            {selectedDay === only.date ? "Clear the day filter" : "Show just that day"}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="px-4 pb-[13px] pt-[15px]">
      <div
        className="flex items-end gap-[3px] border-b"
        style={{ height: PLOT_HEIGHT, borderColor: `${FILL}33` }}
      >
        {daily.map((day) => {
          // Selection dims the others rather than recolouring the chosen one:
          // one series, one hue, and the day is also named in a chip below.
          const dimmed = selectedDay && selectedDay !== day.date;
          return (
            <Bar
              key={day.date}
              type={onSelectDay ? "button" : undefined}
              onClick={
                onSelectDay && day.count
                  ? () => onSelectDay(selectedDay === day.date ? null : day.date)
                  : undefined
              }
              aria-pressed={onSelectDay ? selectedDay === day.date : undefined}
              title={
                day.count
                  ? `${day.date} · ${day.count} inspection${
                      day.count === 1 ? "" : "s"
                    }${onSelectDay ? " — click to filter the list" : ""}`
                  : `${day.date} · nothing captured`
              }
              /* h-full matters: the bar's height is a percentage, and a
                 percentage against an auto-height flex item resolves to zero —
                 which rendered the whole chart invisible. */
              className={`group flex h-full flex-1 items-end ${
                onSelectDay ? "cursor-pointer" : "cursor-default"
              }`}
              style={{
                minWidth: 0,
                flexBasis: `${width}%`,
                maxWidth: MAX_BAR_WIDTH,
              }}
            >
              <div
                className="w-full rounded-t-[4px] transition-opacity group-hover:opacity-80"
                style={{
                  // A floor so a day with one inspection is visible, but ZERO
                  // must draw nothing: with the quiet days now filled in, a
                  // floored zero would look exactly like a day that had one.
                  height: day.count
                    ? Math.max(MIN_BAR, (day.count / max) * PLOT_HEIGHT)
                    : 0,
                  // One series, one colour. Shading these by count would spend
                  // the colour channel restating the height.
                  background: FILL,
                  opacity: dimmed ? 0.35 : 1,
                }}
              />
            </Bar>
          );
        })}
      </div>
      <div
        className="mt-[7px] flex max-w-full justify-between gap-4 text-[10.5px]"
        style={{
          color: INK_MUTED,
          width: Math.max(daily.length * (MAX_BAR_WIDTH + 3), 170),
        }}
      >
        <span>{daily[0]?.date}</span>
        {daily.length > 1 && <span>{daily[daily.length - 1]?.date}</span>}
      </div>
      {onSelectDay && (
        <div className="mt-[6px] text-[10.5px]" style={{ color: INK_MUTED }}>
          {selectedDay
            ? `Showing ${selectedDay} in the list below — click that bar again to clear`
            : "Click a day to see just those inspections"}
        </div>
      )}
    </div>
  );
}

/**
 * "Request failed with status code 404" tells a reader nothing, and 404 is the
 * one status this endpoint actually produces when something is misconfigured:
 * analytics lives on a different host than the rest of motor, and a dev-server
 * that has not restarted since setupProxy.js changed answers 404 too. Both are
 * fixable, so the message says which to check.
 */
const describeFailure = (error) => {
  const status = error?.response?.status;
  if (status === 404) {
    return "This deployment has no analytics route at the host we asked. Check the client → host line logged by [urls]; if setupProxy.js changed, the dev server needs a restart to pick it up.";
  }
  if (status === 401 || status === 403) {
    return "The session is not authorised for analytics on this host.";
  }
  if (!error?.response) {
    return "Analytics could not be reached — the request never got a reply (network, CORS, or the host being down).";
  }
  return error?.message || "Analytics could not be loaded";
};

/**
 * The page itself, given its data.
 *
 * Split out from the fetching shell so the whole thing can be rendered against
 * a saved response — the charts are the part that has to be looked at, and the
 * page needs a login to reach otherwise.
 */
export function AnalyticsBody({
  data,
  inspections,
  facets,
  filters,
  setFilters,
  setDay,
  reviewLabelFor,
  identityLabel,
  isProperty,
  caseBasePath,
}) {
  const busiest = useMemo(
    () =>
      data?.daily?.length
        ? [...data.daily].sort((a, b) => b.count - a.count)[0]
        : null,
    [data],
  );

  if (!data) return null;

  return (
    <>
        <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
          <Tile
            label="Inspections"
            value={data.totals.inspections.toLocaleString()}
            sub={`${data.totals.passed.toLocaleString()} passed · ${data.totals.failed} failed`}
            icon={ClipboardList}
            hue={RAMP.blue[0]}
            visual={<Sparkline points={data.daily} color={RAMP.blue[0]} />}
          />
          <Tile
            label="Pass rate"
            value={pct(data.totals.passRate)}
            sub={
              data.totals.failed
                ? `${data.totals.failed} rejected in this period`
                : "nothing rejected"
            }
            /* A judgement, so it takes a status colour — and the icon that
               says which way the judgement went, because the colour alone is
               not allowed to carry it. */
            tone={
              data.totals.passRate === null
                ? undefined
                : data.totals.passRate < 0.9
                  ? "alert"
                  : "good"
            }
            icon={
              data.totals.passRate !== null && data.totals.passRate < 0.9
                ? XCircle
                : CheckCircle2
            }
            visual={
              data.totals.passRate === null ? null : (
                <Arc
                  value={data.totals.passRate}
                  color={data.totals.passRate < 0.9 ? ALERT : GOOD}
                />
              )
            }
          />
          <Tile
            label="Median inspection"
            icon={Timer}
            hue={RAMP.blue[0]}
            value={formatDuration(data.duration.median) || "—"}
            sub={
              data.duration.recorded
                ? `timed on ${data.duration.recorded} of ${data.duration.of} read`
                : "no durations recorded"
            }
          />
          <Tile
            label="Links completed"
            value={pct(data.links.completionRate)}
            sub={`${data.links.completed} of ${data.links.generated} generated`}
            tone={
              data.links.completionRate === null
                ? undefined
                : data.links.completionRate < 0.5
                  ? "alert"
                  : "good"
            }
            icon={
              data.links.completionRate !== null && data.links.completionRate < 0.5
                ? TimerOff
                : CheckCircle2
            }
            visual={
              data.links.completionRate === null ? null : (
                <Arc
                  value={data.links.completionRate}
                  color={data.links.completionRate < 0.5 ? ALERT : GOOD}
                />
              )
            }
          />
          {/* The surveyors' own verdict, which was a footnote under the charts
              until now. It sits with the other headline figures because it is
              one — with the coverage stated on its face, since it is the
              sparsest field the API returns. */}
          <RatingTile rating={data.rating} />
        </div>

        {/* Coverage, said plainly: the per-row figures below cover only the
            rows returned, and a period larger than one page would under-report. */}
        {!data.coverage.complete && (
          <div
            className="mt-3 flex items-start gap-[8px] rounded-tile border px-[11px] py-[9px]"
            style={{
              background: wash(RAMP.blue[0]),
              borderColor: `${RAMP.blue[0]}33`,
            }}
          >
            <Info
              className="mt-[1px] h-[13px] w-[13px] flex-none"
              style={{ color: RAMP.blue[0] }}
            />
            <p className="text-[12px] leading-[1.5] text-cl-secondary">
              The four figures above cover the whole period. Everything below
              them is worked out from the{" "}
              <span className="font-semibold">
                {data.coverage.rows.toLocaleString()} inspections
              </span>{" "}
              read of {data.coverage.total.toLocaleString()} — so treat the
              breakdowns as a sample, not a census.
            </p>
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
          {data.daily.length > 0 && (
            <div className={`${card} xl:col-span-2`}>
              <CardHead
                icon={CalendarDays}
                hue={RAMP.blue[0]}
                title="Inspections per day"
                hint={
                  data.daily.length === 1
                    ? `${busiest?.count ?? 0} on the one day`
                    : `${data.daysWithActivity} of ${data.daily.length} days had inspections${
                        busiest ? ` · busiest ${busiest.date}, ${busiest.count}` : ""
                      }`
                }
              />
              <DailyVolume
                daily={data.daily}
                selectedDay={filters.day}
                onSelectDay={setDay}
              />
            </div>
          )}

          {/* The capture-link funnel. Its own block because the numbers are
              about how links reach people, not about the inspections. */}
          <div className={card}>
            <CardHead
              icon={Link2}
              hue={RAMP.blue[0]}
              title="Capture links"
              hint={`${data.links.generated} sent to customers`}
            />
            <div className="py-[6px]">
              {[
                ["Completed", data.links.completed, CheckCircle2, FILL],
                [
                  "Started, not finished",
                  data.links.startedNotCompleted,
                  Clock,
                  FILL,
                ],
                ["Never opened", data.links.notStarted, CircleDashed, FILL],
                // The one row that is a problem, so the one row in red — with
                // its own icon and words, never colour alone. Blue beside red
                // is also the only pair the validator clears for one chart.
                ["Expired unused", data.links.expired, TimerOff, ALERT],
              ].map(([label, count, icon, color]) => (
                <BarRow
                  key={label}
                  label={label}
                  count={count}
                  max={data.links.generated}
                  icon={icon}
                  color={color}
                />
              ))}
            </div>
            {data.links.expired > data.links.completed && (
              <p className="border-t border-cl-row px-4 py-[10px] text-[11.5px] leading-[1.5] text-cl-muted">
                More links expired than were used. That is a delivery problem
                rather than an inspection one.
              </p>
            )}
          </div>

          {/* What goes wrong at capture — the actionable half of this page. */}
          {data.warnings.length > 0 && (
            <div className={card}>
              <CardHead
                icon={AlertTriangle}
                hue={RAMP.amber[0]}
                title="What went wrong during capture"
                hint={capped(data.warnings.length, 8, "click one to see those inspections")}
              />
              {/* A bar counts inspections carrying that warning, so the bars
                  cannot add up to anything meaningful. How many inspections
                  were affected at all is the number a reader wants, and it is
                  the one thing the chart cannot show. */}
              {facets.total > 0 && (
                <p className="border-b border-cl-row px-4 py-[9px] text-[11.5px] leading-[1.5] text-cl-muted">
                  {facets.withWarning} of {facets.total} inspections read raised at
                  least one warning. One inspection can appear in several bars.
                </p>
              )}
              <div className="py-[6px]">
                {data.warnings.slice(0, 8).map((warning) => (
                  <BarRow
                    key={warning.code}
                    label={warning.label}
                    count={warning.count}
                    max={data.warnings[0].count}
                    color={RAMP.amber[0]}
                    active={filters.warning === warning.code}
                    onClick={() =>
                      setFilters((current) => ({
                        ...current,
                        warning:
                          current.warning === warning.code ? "all" : warning.code,
                      }))
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {data.failureReasons.length > 0 && (
            <div className={card}>
              <CardHead
                icon={XCircle}
                hue={RAMP.red[0]}
                title="Why inspections failed"
                hint={`${data.totals.failed} rejected · click to see them`}
              />
              <div className="py-[6px]">
                {data.failureReasons.map((reason) => (
                  <BarRow
                    key={reason.reason}
                    label={reason.reason}
                    count={reason.count}
                    max={data.failureReasons[0].count}
                    color={ALERT}
                    active={filters.result === "failed"}
                    onClick={() =>
                      setFilters((current) => ({
                        ...current,
                        result: current.result === "failed" ? "all" : "failed",
                      }))
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* Motor only: a property inspection reports no panels, so these
              are absent rather than empty. */}
          {data.panels.length > 0 && (
            <div className={card}>
              <CardHead
                icon={Car}
                hue={RAMP.magenta[0]}
                title="Most damaged panels"
                hint={capped(data.panels.length, 8, "click one to see those inspections")}
              />
              <div className="py-[6px]">
                {data.panels.slice(0, 8).map((panel) => (
                  <BarRow
                    key={panel.name}
                    label={panel.label}
                    count={panel.count}
                    max={data.panels[0].count}
                    color={RAMP.magenta[0]}
                    active={filters.part === panel.name}
                    onClick={() =>
                      setFilters((current) => ({
                        ...current,
                        part: current.part === panel.name ? "all" : panel.name,
                      }))
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* How the ratings fall, not just their average: a mean of 3.0 looks
              the same whether every surveyor said 3, or half said 1 and half
              said 5 — and those are different problems. */}
          {data.rating.recorded > 0 && (
            <div className={card}>
              <CardHead
                icon={Star}
                hue={RATING}
                title="How surveyors rated the inspections"
                hint={`${data.rating.recorded} rated of ${data.rating.of} read`}
              />
              <div className="py-[6px]">
                {data.rating.distribution.map((step) => (
                  <BarRow
                    key={step.score}
                    label={`${step.score} out of 5`}
                    count={step.count}
                    max={Math.max(
                      ...data.rating.distribution.map((entry) => entry.count),
                      1,
                    )}
                    /* Colour follows the tier, never the count: a 5 is the
                       deepest step whether one surveyor gave it or fifty. */
                    color={depth(RAMP.amber, step.score, 5)}
                    active={filters.rating === String(step.score)}
                    onClick={() =>
                      setFilters((current) => ({
                        ...current,
                        rating:
                          current.rating === String(step.score)
                            ? "all"
                            : String(step.score),
                      }))
                    }
                  />
                ))}
              </div>
              {data.rating.recorded < 5 && (
                <p className="border-t border-cl-row px-4 py-[10px] text-[11.5px] leading-[1.5] text-cl-muted">
                  Only {data.rating.recorded} inspection
                  {data.rating.recorded === 1 ? " was" : "s were"} rated, so this
                  is too little to read as a trend.
                </p>
              )}
            </div>
          )}

          {data.damageTypes.length > 0 && (
            <div className={card}>
              <CardHead
                icon={Wrench}
                hue={RAMP.magenta[0]}
                title="Damage types"
                hint={capped(data.damageTypes.length, 8, "across the rows read")}
              />
              <div className="py-[6px]">
                {data.damageTypes.slice(0, 8).map((type) => (
                  <BarRow
                    key={type.type}
                    label={type.label}
                    count={type.count}
                    max={data.damageTypes[0].count}
                    color={RAMP.magenta[0]}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* The evidence behind the charts above. Everything up there is an
            aggregate; this is the list of actual inspections, and the charts
            narrow it rather than opening a separate view. */}
        <div className="mt-4">
          <InspectionsTable
            inspections={inspections}
            facets={facets}
            filters={filters}
            onChange={setFilters}
            identityLabel={identityLabel}
            showPanels={!isProperty}
            reviewLabelFor={reviewLabelFor}
            coverage={data.coverage}
            caseBasePath={caseBasePath}
          />
        </div>

    </>
  );
}

const RANGE_DAYS = 30;

export default function AnalyticsDashboard() {
  const client = getClientEntry(getSourceName());
  const isProperty = client?.inspectionLine === "property";

  const [range, setRange] = useState(() => {
    const to = new Date();
    const from = new Date(to.getTime() - RANGE_DAYS * 86400000);
    return { from: iso(from), to: iso(to) };
  });
  const [state, setState] = useState({
    loading: true,
    error: null,
    data: null,
    inspections: [],
  });
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });

  // Motor identifies a row by plate, property by address — the same field the
  // case list uses, read from the client file rather than guessed from the line.
  const identityField = client?.listIdentityField || "num_plate";
  const identityLabel = identityField === "address" ? "Address" : "Plate";

  const load = useCallback(async () => {
    setState({ loading: true, error: null, data: null, inspections: [] });
    try {
      const raw = await getAnalyticsData({
        startDate: range.from,
        stopDate: range.to,
      });
      setState({
        loading: false,
        error: null,
        data: toAnalytics(raw),
        inspections: toInspections(raw, { identityField }),
      });
    } catch (error) {
      setState({
        loading: false,
        error: describeFailure(error),
        data: null,
        inspections: [],
      });
    }
  }, [range.from, range.to, identityField]);

  useEffect(() => {
    load();
    // A new period is a new set of rows: a day or a warning code carried over
    // from the last one would silently filter everything out.
    setFilters({ ...EMPTY_FILTERS });
  }, [load]);

  const { loading, error, data, inspections } = state;

  // This page is mounted under two prefixes (/analytics and
  // /redesign/analytics), so a case link should keep the reader in the design
  // they are already looking at rather than throwing them into the other one.
  const { pathname } = useLocation();
  const caseBasePath = pathname.startsWith("/redesign")
    ? "/redesign/cases"
    : "/cases";

  // A claim_state is a number until the client says what it means; property
  // calls 3 "Completed" where motor's review pipeline calls it "AI Assessed".
  const reviewLabelFor = useCallback(
    (stateCode) =>
      getStatusInfoFor(stateCode, client?.statusLabels).text || String(stateCode),
    [client],
  );

  const facets = useMemo(
    () => facetsOf(inspections, { reviewLabel: reviewLabelFor }),
    [inspections, reviewLabelFor],
  );

  // The date range at the top of the page is served by the API; this narrows
  // within it. Both are "date", so the day chip lives with the other filters.
  // What the report will contain: the rows the reader has filtered to, in the
  // order the table shows them. Same helpers the table uses, so the file and
  // the screen can never disagree.
  const reported = useMemo(
    () => sortInspections(applyFilters(inspections, filters), { key: "date", dir: "desc" }),
    [inspections, filters],
  );

  const reportHtml = useCallback(
    () =>
      buildReportHtml({
        clientLabel: client?.label || "Ergo",
        line: client?.inspectionLine || "motor",
        range,
        data,
        inspections: reported,
        facets,
        filters,
        identityLabel,
        reviewLabelFor,
        generatedAt: new Date().toLocaleString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      }),
    [client, range, data, reported, facets, filters, identityLabel, reviewLabelFor],
  );

  const [printing, setPrinting] = useState(false);

  const onDownloadHtml = useCallback(() => {
    const html = reportHtml();
    if (!html) return;
    downloadFile(html, reportFileName(client?.label || "Ergo", range, "html"));
  }, [reportHtml, client, range]);

  const onDownloadPdf = useCallback(() => {
    const html = reportHtml();
    if (!html) return;
    setPrinting(true);
    printHtml(html, { onDone: () => setPrinting(false) });
  }, [reportHtml]);

  const setDay = useCallback(
    (day) => setFilters((current) => ({ ...current, day })),
    [],
  );

  return (
    <div className="cl-scroll h-full overflow-y-auto px-8 pb-10 pt-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold tracking-[-0.01em] text-cl-text">
            Analytics
          </h1>
          <p className="mt-[2px] text-[12.5px] text-cl-muted">
            {isProperty ? "Property inspections" : "Motor inspections"} ·{" "}
            {client?.label || "Ergo"}
          </p>
        </div>

        {/* The date range, then the two ways out of the page. */}
        <div className="flex flex-wrap items-end gap-3">
          {[
            ["From", "from"],
            ["To", "to"],
          ].map(([label, key]) => (
            <label key={key} className="flex flex-col gap-[4px]">
              <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                {label}
              </span>
              <input
                type="date"
                value={range[key]}
                onChange={(event) =>
                  setRange((current) => ({ ...current, [key]: event.target.value }))
                }
                className="cl-date-input rounded-tile border border-cl-control bg-cl-surface px-[10px] py-[6px] text-[12.5px] text-cl-text outline-none focus:border-cl-accent"
              />
            </label>
          ))}

          {/* Both downloads are the same document — one saved as a file, one
              handed to the browser's print pipeline. Disabled until there is
              something to report, because a report of nothing is a support
              ticket. */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onDownloadPdf}
              disabled={!data || loading || printing}
              title={
                data
                  ? `${reportTitle(client?.label || "Ergo", client?.inspectionLine, range)} — opens your print dialog, where you choose Save as PDF`
                  : "Nothing to report yet"
              }
              className="inline-flex items-center gap-[6px] rounded-tile border border-cl-control bg-cl-surface px-[11px] py-[6px] text-[12px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover hover:text-cl-text disabled:cursor-not-allowed disabled:opacity-50"
            >
              {printing ? (
                <Loader2 className="h-[13px] w-[13px] animate-spin" />
              ) : (
                <FileText className="h-[13px] w-[13px]" />
              )}
              PDF
            </button>
            <button
              type="button"
              onClick={onDownloadHtml}
              disabled={!data || loading}
              title={
                data
                  ? "Saves the whole report as a single self-contained HTML file"
                  : "Nothing to report yet"
              }
              className="inline-flex items-center gap-[6px] rounded-tile border border-cl-control bg-cl-surface px-[11px] py-[6px] text-[12px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover hover:text-cl-text disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download className="h-[13px] w-[13px]" />
              HTML
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-3 py-20 text-[13px] text-cl-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Reading inspections…
        </div>
      )}

      {error && !loading && (
        <div className={`${card} mt-5 px-4 py-[15px]`}>
          <div className="flex items-start gap-2">
            <AlertTriangle
              className="mt-[2px] h-[13px] w-[13px] flex-none"
              style={{ color: WARN_INK }}
            />
            <p className="text-[12.5px] leading-[1.5] text-cl-secondary">{error}</p>
          </div>
          <button
            type="button"
            onClick={load}
            className="mt-3 rounded-tile border border-cl-control px-[13px] py-[6px] text-[12px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover"
          >
            Try again
          </button>
        </div>
      )}

      {data && !loading && (
        <AnalyticsBody
          data={data}
          inspections={inspections}
          facets={facets}
          filters={filters}
          setFilters={setFilters}
          setDay={setDay}
          reviewLabelFor={reviewLabelFor}
          identityLabel={identityLabel}
          isProperty={isProperty}
          caseBasePath={caseBasePath}
        />
      )}
    </div>
  );
}
